import {Card, Table} from 'react-bootstrap'
import {retentionTextColor, inverseTextColor, renovationTextColor} from './dashboardColors'

interface IBreakdownKpi {
  name: string
  newPolicies: number | null
  newPoliciesPrev: number | null
  cancellations: number | null
  atRiskCount: number | null
  neverStartedCount: number | null
  cancellationsPct: number | null
  churnRate: number | null
  retention3mPct: number | null
  retention6mPct: number | null
  renovationsPct: number | null
}

interface IBreakdownKpiTableProps {
  title: string
  groupLabel: string
  rows: IBreakdownKpi[]
  loading: boolean
}

const ROW_HEIGHT = 28
const MAX_VISIBLE_ROWS = 10

const pctCell = (val: number | null, invert = false, colorFn?: (v: number) => string) => {
  const v = val ?? 0
  const color = colorFn ? colorFn(v) : invert ? inverseTextColor(v) : retentionTextColor(v)
  return <span style={{color, fontWeight: 600}}>{v.toFixed(0)}%</span>
}

// Compact cell padding via inline style — Bootstrap's table-sm still leaves too much horizontal space
const th: React.CSSProperties = {padding: '4px 6px', whiteSpace: 'normal', lineHeight: 1.2, textAlign: 'center' as const}
const td: React.CSSProperties = {padding: '3px 6px', whiteSpace: 'nowrap'}

const TrendCell = ({current, prev}: {current: number; prev: number | null}) => {
  if (prev === null || prev === undefined) return <span style={{color: '#a1a5b7'}}>—</span>
  const delta = current - prev
  if (delta === 0) return <span style={{color: '#a1a5b7', fontSize: '0.7rem'}}>= 0</span>
  const up = delta > 0
  return (
    <span style={{color: up ? '#1bc5bd' : '#f64e60', fontWeight: 600, fontSize: '0.72rem'}}>
      {up ? '↑' : '↓'} {up ? '+' : ''}{delta.toLocaleString()}
    </span>
  )
}

export const BreakdownKpiTable = ({title, groupLabel, rows, loading}: IBreakdownKpiTableProps) => {
  if (loading || rows.length === 0) return null

  const totalNew = rows.reduce((s, r) => s + (r.newPolicies ?? 0), 0)
  const scrollable = rows.length > MAX_VISIBLE_ROWS

  return (
    <Card className='card-flush'>
      <Card.Header className='border-0 pt-4 pb-2'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-6 mb-0'>{title}</span>
        </h3>
      </Card.Header>
      <Card.Body className='pt-0 pb-2'>
        <div
          className='table-responsive'
          style={scrollable ? {maxHeight: MAX_VISIBLE_ROWS * ROW_HEIGHT, overflowY: 'auto'} : undefined}
        >
          <Table hover className='align-middle mb-0' style={{fontSize: '0.75rem'}}>
            <thead style={{position: 'sticky', top: 0, background: '#fff', zIndex: 1}}>
              <tr className='text-muted fw-bold text-uppercase' style={{fontSize: '0.68rem'}}>
                <th style={{...th, textAlign: 'left'}}>{groupLabel}</th>
                <th style={th}>New</th>
                <th style={th}>vs<br/>Prev</th>
                <th style={th}>Share</th>
                <th style={th}>Can-<br/>cels</th>
                <th style={th}>At<br/>Risk</th>
                <th style={th}>Never<br/>Started</th>
                <th style={th}>Active</th>
                <th style={th}>Cohort<br/>3m</th>
                <th style={th}>Cohort<br/>6m</th>
                <th style={th}>Renew<br/>als%</th>
                <th style={th}>Can-<br/>cels%</th>
                <th style={th}>Churn</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const newPolicies = row.newPolicies ?? 0
                const cancellations = row.cancellations ?? 0
                const atRisk = row.atRiskCount ?? 0
                const neverStarted = row.neverStartedCount ?? 0
                const netGrowth = newPolicies - cancellations - neverStarted
                const sharePct = totalNew > 0 ? Math.round(newPolicies / totalNew * 100) : 0
                return (
                  <tr key={row.name}>
                    <td style={{...td, fontWeight: 600}}>{row.name}</td>
                    <td style={{...td, textAlign: 'right'}}>{newPolicies.toLocaleString()}</td>
                    <td style={{...td, textAlign: 'center'}}>
                      <TrendCell current={newPolicies} prev={row.newPoliciesPrev} />
                    </td>
                    <td style={{...td, textAlign: 'right', color: '#a1a5b7', fontWeight: 600}}>{sharePct}%</td>
                    <td style={{...td, textAlign: 'right', color: '#f64e60', fontWeight: 600}}>{cancellations.toLocaleString()}</td>
                    <td style={{...td, textAlign: 'right', color: '#ffa800', fontWeight: 600}}>{atRisk.toLocaleString()}</td>
                    <td style={{...td, textAlign: 'right', color: '#a1a5b7', fontWeight: 600}}>{neverStarted.toLocaleString()}</td>
                    <td style={{...td, textAlign: 'right', fontWeight: 600, color: netGrowth >= 0 ? '#1bc5bd' : '#f64e60'}}>
                      {netGrowth >= 0 ? `+${netGrowth.toLocaleString()}` : netGrowth.toLocaleString()}
                    </td>
                    <td style={{...td, textAlign: 'right'}}>{pctCell(row.retention3mPct)}</td>
                    <td style={{...td, textAlign: 'right'}}>{pctCell(row.retention6mPct)}</td>
                    <td style={{...td, textAlign: 'right'}}>{pctCell(row.renovationsPct, false, renovationTextColor)}</td>
                    <td style={{...td, textAlign: 'right'}}>{pctCell(row.cancellationsPct, true)}</td>
                    <td style={{...td, textAlign: 'right'}}>{pctCell(row.churnRate, true)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  )
}
